import React, { useEffect, useState } from "react";
import { createContext, useContext } from "react";
import useLocalStorage from "./useLocalStorage";
import { useRequest } from "../hooks/useRequest";

const AppContext = createContext();

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

const AppContextProvider = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [user, setUser] = useLocalStorage("user", "");
  const [isAuthForm, setIsAuthForm] = useState(false);
  const [isResetForm, setIsResetForm] = useState(false);
  const [checkoutCartData, setCheckoutCartData] = useState();
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [appliedReward, setAppliedReward] = useState(0);
  const [categoriesData, setCategoriesData] = useState([]);
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [thankyouData, setThankyouData] = useState();
  const [cartItemNumber, setCartItemNumber] = useState(0);
  const [fetchCategories] = useRequest();

  const handleWindowResize = () => {
    const windowWidth = window.innerWidth;

    setIsMobile(windowWidth < 768);
    setIsTablet(windowWidth >= 768 && windowWidth <= 1024);
    setIsDesktop(windowWidth > 1024);
  };

  useEffect(() => {
    handleWindowResize();

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  // Fetch categories data with caching
  useEffect(() => {
    const fetchCategoriesData = async () => {
      if (categoriesTree?.length || categoriesLoading) {
        return;
      }

      // Check cache first
      const cachedData = localStorage.getItem('categoriesCache');
      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        const isCacheValid = Date.now() - timestamp < CACHE_DURATION;

        if (isCacheValid) {
          setCategoriesTree(data);
          setCategoriesLoading(false);
          return;
        }
      }

      setCategoriesLoading(true);
      try {
        const categoryData = await fetchCategories({
          path: `/category/categories/json-tree-updated`,
        });

        if (categoryData?.data) {
          setCategoriesTree(categoryData.data);
          // Cache the data
          localStorage.setItem('categoriesCache', JSON.stringify({
            data: categoryData.data,
            timestamp: Date.now()
          }));
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategoriesData();
  }, []);

  const appContextValues = {
    isMobile,
    isDesktop,
    isTablet,
    user,
    setUser,
    isAuthForm,
    setIsAuthForm,
    checkoutCartData,
    setCheckoutCartData,
    appliedCoupon,
    setAppliedCoupon,
    categoriesData,
    setCategoriesData,
    thankyouData,
    setThankyouData,
    isResetForm,
    setIsResetForm,
    appliedReward,
    setAppliedReward,
    cartItemNumber,
    setCartItemNumber,
    categoriesLoading,
    setCategoriesLoading,
    categoriesTree,
    setCategoriesTree,
  };

  return (
    <AppContext.Provider value={appContextValues}>
      {children}
    </AppContext.Provider>
  );
};

const useAppContext = () => useContext(AppContext);

export { useAppContext, AppContextProvider };
